import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PLATFORMS = [
  { value: "shopee", label: "Shopee" },
  { value: "tiktok", label: "TikTok Shop" },
  { value: "tokopedia", label: "Tokopedia" },
  { value: "lazada", label: "Lazada" },
];

function StoreMappingTab({ canWrite }) {
  const [mappings, setMappings] = useState([]);
  const [unconfigured, setUnconfigured] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCustName, setSelectedCustName] = useState("");
  const [showCust, setShowCust] = useState(false);
  const [form, setForm] = useState({ platform: "shopee", customer_id: "", warehouse_id: "", shop_account: "" });

  const load = async () => {
    try {
      const [mapRes, whRes] = await Promise.all([
        api.get("/marketplace-shipping/store-mappings"),
        api.get("/warehouses"),
      ]);
      setMappings(mapRes.data.mappings || []);
      setUnconfigured(mapRes.data.unconfigured || []);
      setWarehouses(whRes.data || []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const platformOf = (mp = "") => {
    const s = mp.toLowerCase();
    if (s.includes("shopee")) return "shopee";
    if (s.includes("tiktok")) return "tiktok";
    if (s.includes("tokopedia")) return "tokopedia";
    if (s.includes("lazada")) return "lazada";
    return s.split(" ")[0] || "shopee";
  };

  const searchCustomers = async (query) => {
    setCustQuery(query);
    setShowCust(true);
    if (query.trim().length < 1) {
      setCustResults([]);
      return;
    }
    try {
      const res = await api.get("/customers", { params: { q: query, limit: 30 } });
      const arr = Array.isArray(res.data) ? res.data : (res.data.items || res.data.rows || []);
      setCustResults(arr.slice(0, 30));
    } catch {
      setCustResults([]);
    }
  };

  const pickCustomer = (c, platform) => {
    setForm((f) => ({ ...f, customer_id: c.id, platform: platform || f.platform }));
    setSelectedCustName(c.name);
    setCustQuery(c.name);
    setShowCust(false);
    setCustResults([]);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.customer_id) {
      toast.error("Pilih customer toko");
      return;
    }
    try {
      await api.post("/marketplace-shipping/store-mappings", form);
      toast.success("Mapping toko dibuat");
      setForm({ platform: form.platform, customer_id: "", warehouse_id: "", shop_account: "" });
      setCustQuery("");
      setSelectedCustName("");
      setCustResults([]);
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/marketplace-shipping/store-mappings/${id}`);
      toast.success("Mapping dihapus");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5">
      {canWrite && (
        <Panel className="relative z-20 overflow-visible p-4" data-testid="store-mapping-form">
          <div className="mb-3 font-semibold text-cocoa">Tambah Mapping Toko</div>
          <div className="grid gap-3 md:grid-cols-[150px_minmax(200px,1fr)_minmax(180px,1fr)_150px_auto] md:items-end">
            <div>
              <Label>Platform</Label>
              <Select onValueChange={(v) => setForm((f) => ({ ...f, platform: v }))} value={form.platform}>
                <SelectTrigger data-testid="store-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer (Toko ERP)</Label>
              <Input
                autoComplete="off"
                data-testid="store-customer-search"
                onBlur={() => setTimeout(() => setShowCust(false), 200)}
                onChange={(e) => searchCustomers(e.target.value)}
                onFocus={() => setShowCust(true)}
                placeholder={selectedCustName || "Ketik nama customer..."}
                value={custQuery}
              />
              {showCust && custQuery.trim().length > 0 && (
                <div className="mt-1 max-h-60 w-full overflow-auto rounded border border-taupe/40 bg-ivory shadow-lg">
                  {custResults.map((c) => (
                    <button
                      className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-taupe/10 ${form.customer_id === c.id ? "bg-taupe/10 font-medium" : ""}`}
                      data-testid={`store-customer-option-${c.id}`}
                      key={c.id}
                      onClick={() => pickCustomer(c)}
                      type="button"
                    >
                      {c.name}{c.marketplace ? ` · ${c.marketplace}` : ""}
                    </button>
                  ))}
                  {custResults.length === 0 && (
                    <div className="px-3 py-2 text-sm text-wmuted">Tidak ada hasil</div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>Gudang (opsional)</Label>
              <Select onValueChange={(v) => setForm((f) => ({ ...f, warehouse_id: v === "none" ? "" : v }))} value={form.warehouse_id || "none"}>
                <SelectTrigger data-testid="store-warehouse-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tidak diset -</SelectItem>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Akun Toko</Label>
              <Input
                data-testid="store-account-input"
                onChange={(e) => setForm((f) => ({ ...f, shop_account: e.target.value }))}
                placeholder="mis. Ventedaily"
                value={form.shop_account}
              />
            </div>
            <Button className="bg-cocoa text-ivory hover:bg-cocoa-dark" data-testid="store-mapping-save" onClick={create}>
              <Plus className="mr-1 h-4 w-4" />Simpan
            </Button>
          </div>
        </Panel>
      )}

      <Panel className="p-4">
        <div className="mb-2 font-semibold text-cocoa">Mapping Toko Aktif</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Platform</TableHead>
              <TableHead>Customer (Toko)</TableHead>
              <TableHead>Akun</TableHead>
              <TableHead>Gudang</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.length === 0 && (
              <TableRow><TableCell className="text-center text-wmuted" colSpan={5}>Belum ada mapping toko</TableCell></TableRow>
            )}
            {mappings.map((m) => (
              <TableRow key={m.id} data-testid={`store-mapping-row-${m.id}`}>
                <TableCell className="uppercase">{m.platform}</TableCell>
                <TableCell className="font-medium text-cocoa">{m.customer_name}</TableCell>
                <TableCell>{m.shop_account || "-"}</TableCell>
                <TableCell>{m.warehouse_name || "-"}</TableCell>
                <TableCell className="text-right">
                  {canWrite && (
                    <Button data-testid={`store-mapping-delete-${m.id}`} onClick={() => remove(m.id)} size="sm" variant="ghost">
                      <Trash2 className="h-4 w-4 text-status-reject" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>

      {unconfigured.length > 0 && (
        <Panel className="p-4" data-testid="store-unconfigured">
          <div className="mb-2 font-semibold text-cocoa">Belum Dikonfigurasi</div>
          <div className="text-sm text-wmuted">Customer jalur marketplace ini belum dipetakan. Pilih salah satu untuk membuat mapping.</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {unconfigured.map((c) => (
              <button
                className="rounded-full border border-taupe/40 px-3 py-1 text-sm text-cocoa transition-colors hover:bg-cocoa hover:text-ivory"
                data-testid={`store-unconfigured-${c.id}`}
                key={c.id}
                onClick={() => pickCustomer(c, platformOf(c.marketplace))}
                title="Klik untuk mengisi form mapping"
                type="button"
              >
                {c.name} · {c.marketplace || "-"}
              </button>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

export default function MarketplaceMapping() {
  const { can } = useAuth();
  const canWrite = can("accounting", "warehouse_admin");

  return (
    <div data-testid="marketplace-mapping-page">
      <PageHeader
        title="Mapping Toko Marketplace"
        subtitle="Petakan toko marketplace ke customer ERP untuk verifikasi isi order"
      />
      <StoreMappingTab canWrite={canWrite} />
    </div>
  );
}
